import { Component, Inject, Injector, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TemplateMessageObject } from '@trendbuild/trend-cloud-api';
import { AbstractComponent, Attendance, Channel, Company, Contact, MessageHelper, MessageScheduled, MessageScheduledService, MessageScheduledStatusEnum, MessageTypeEnum, MetadataMessage, User, waTemplateParamValues } from 'lib-trend-core';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'template-schedule',
    // templateUrl: './chat-template-schedule.component.html',
    templateUrl: './chat-template-schedule.v2.component.html',
    styleUrls: ['./chat-template-schedule.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatTemplateScheduleComponent extends AbstractComponent {
  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  private message: MessageScheduled;
  loadingSpinner$ = this.loadingSpinnerSubject.asObservable();
  selectedContact: Contact = null;
  selectedChannel: Channel;
  metadataMessage: MetadataMessage;
  variables: Record<string, string> = {};
  selectedTemplate: TemplateMessageObject = undefined;
  hourErrorMessage: string = '';

  previewHeader: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { attendance?: Attendance; message?: MessageScheduled },
    injector: Injector,
    public _matDialogRef: MatDialogRef<MessageScheduled>,
    private fb: FormBuilder,
    private messageScgeduledService: MessageScheduledService,
  ) {
    super(injector);
    this.message = data.message
    this.formGroup = this.fb.group({
      scheduledDay: ['', Validators.required],
      scheduledHour: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    if (!this.isNew) {
      this.formGroup.patchValue({
        scheduledDay: this.data?.message?.scheduledDate,
        scheduledHour: this.data?.message?.scheduledTime,
      });

      if (this.data.message && this.data.message.metadata) {
        this.selectedTemplate = this.data.message.metadata.template;
        this.setTemplateMessage(this.data.message.metadata);
        this.buildVariables();
      }
    }

    this.formGroup.get('scheduledHour')?.valueChanges.subscribe(value => {
      this.validateHour(value);
    });
  }

  saveMessageTemplateScheduled(): void {
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }

    const scheduledDay = this.formGroup.get('scheduledDay')?.value;
    const scheduledHourRaw = this.formGroup.get('scheduledHour')?.value;

    if (!scheduledDay || !scheduledHourRaw) {
      this.alertService.warning(this.t('attendance.chatTemplateSchedule.scheduledDateTimeRequired'));
      return;
    }

    let scheduledHour = scheduledHourRaw.replace(/^(\d{2})(\d{2})$/, '$1:$2');

    const validHourPattern = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):([0-9]|[0-5][0-9])$/;
    if (!validHourPattern.test(scheduledHour)) {
      this.alertService.error(this.t('attendance.chatTemplateSchedule.validHourFormat'));
      return;
    }
    
    const [hourPart, minutePart] = scheduledHour.split(':');
    scheduledHour = `${hourPart.padStart(2, '0')}:${minutePart.padStart(2, '0')}`;

    const [hour, minute] = scheduledHour.split(':').map(Number);
    const scheduledDateTime = new Date(scheduledDay);
    scheduledDateTime.setHours(hour, minute, 0, 0);

    const currentTime = new Date();
    currentTime.setMinutes(currentTime.getMinutes() + 2);

    if (scheduledDateTime < currentTime) {
      this.alertService.error(this.t('attendance.chatTemplateSchedule.scheduledDateTimeMinTwoMinutes'));
      return;
    }

    if (!this.selectedTemplate) {
      this.alertService.warning(this.t('attendance.chatTemplateSchedule.selectTemplateBeforeScheduling'));
      return;
    }

    // The params are an array (POSITIONAL) or an object (NAMED); the checks read the values alone.
    const headerValues = waTemplateParamValues(this.metadataMessage.headerParams);
    const bodyValues = waTemplateParamValues(this.metadataMessage.bodyParams);

    if (!this.metadataMessage.headerParams || headerValues.includes(null) || headerValues.includes('')) {
      this.alertService.warning(this.t('attendance.chatTemplateSchedule.fillHeaderVariablesBeforeScheduling'));
      return;
    }

    if (!this.metadataMessage.bodyParams || bodyValues.includes(null) || bodyValues.includes('')) {
      this.alertService.warning(this.t('attendance.chatTemplateSchedule.fillBodyVariablesBeforeScheduling'));
      return;
    }

    this.loading = true;
    const messageTemplateScheduled = <MessageScheduled>{
      content: headerValues[0] ?? this.metadataMessage?.previewText,
      contentType: MessageTypeEnum.template,
      contact: { _id: this.data.attendance.contact._id } as Contact,
      channel: { _id: this.data.attendance.channel._id } as Channel,
      company: { _id: super.getIDCurrentCompany() } as Company,
      attendance: { _id: this.data.attendance._id } as Attendance,
      user: { _id: super.getIDCurrentUser() } as User,
      type: MessageTypeEnum.template,
      removed: false,
      scheduledDate: scheduledDay,
      scheduledTime: scheduledHour,
      status: MessageScheduledStatusEnum.WAITING,
      metadata: this?.metadataMessage,
    };

    this.messageScgeduledService.create(messageTemplateScheduled).subscribe({
      next: () => {
        this.alertService.success(this.t('attendance.chatTemplateSchedule.scheduledMessageCreated'));
        this._matDialogRef.close();
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao agendar mensagem:', error);
        this.alertService.error(this.t('attendance.chatTemplateSchedule.scheduleMessageError'));
        this.loading = false;
      },
    });
  }

  setTemplateMessage(metadataMessage: MetadataMessage) {
    this.metadataMessage = metadataMessage;
  }

  onSelectTemplate(template: TemplateMessageObject) {
    this.selectedTemplate = template;
    this.buildVariables();
  }

  changeTemplate() {
    this.selectedTemplate = null;
  }

  private buildVariables(): void {
    this.variables = {
      'Saudação': MessageHelper.getGreeting(),
    };
    if (!!this.data.attendance?.user) {
      this.variables['Nome do Atendente'] = this.data.attendance.user.name;
    }
    if (!!this.data.attendance?.department) {
      this.variables['Departamento'] = this.data.attendance.department.name;
    }
    if (!!this.data.attendance.protocol) {
      this.variables['Protocolo'] = this.data.attendance.protocol;
    }
  }

  validateHour(value: string): void {
    this.hourErrorMessage = '';
    
    if (!value) {
      return;
    }
    
    if (value.length === 4 && !value.includes(':')) {
      value = value.substring(0, 2) + ':' + value.substring(2);
    }
    
    const hourParts = value.split(':');
    
    if (hourParts.length !== 2) {
      this.hourErrorMessage = 'Formato inválido, use HH:MM (exemplo: 08:00)';
      return;
    }
    
    const hours = parseInt(hourParts[0], 10);
    const minutes = parseInt(hourParts[1], 10);
    
    if (isNaN(hours) || isNaN(minutes)) {
      this.hourErrorMessage = 'Formato inválido, use HH:MM (exemplo: 08:00)';
      return;
    }
    
    if (hours < 0 || hours > 23) {
      this.hourErrorMessage = 'A hora deve estar entre 00:00 e 23:59';
      return;
    }
    
    if (minutes < 0 || minutes > 59) {
      this.hourErrorMessage = 'Os minutos devem estar entre 00 e 59';
      return;
    }
  }

}
